/**
 * Settings → Chat — funciones del orquestador por tenant (Sprint 267, REQ-CAP-002 §4; ISS-572).
 *
 * Política global del tenant por capacidad (activación + settings por defecto,
 * renderizados desde `config_schema`). Es la base de la herencia
 * tenant → hive → agente: los agentes/hives sin decisión explícita la reflejan.
 *
 * Vista compacta (patrón "Skills del hive") + modal de configuración (patrón
 * "Skills scope"). Consume `GET /api/v1/tenants/{t}/capabilities` y
 * `PUT .../capabilities/{slug}`.
 */

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button, Card, Spinner } from '@/components/ui';
import { Checkbox } from '@/components/ui/checkbox';
import { useI18n } from '@/i18n';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { useChatSettings, useUpdateChatSettings } from '@/features/chat/hooks/useChatSettings';
import { ChatRetentionCard } from '@/features/chat/components/ChatRetentionCard';
import { getErrorMessage } from '@/lib/api-client';
import { CapabilitiesModal } from '@/features/capabilities/CapabilitiesModal';
import { CapabilityCompactList } from '@/features/capabilities/CapabilityCompactList';
import { CapabilitySettingsForm } from '@/features/capabilities/CapabilitySettingsForm';
import { capabilityTitle } from '@/features/capabilities/CapabilityRow';
import { useCapabilitiesCatalog, useSetTenantPolicy } from '@/features/capabilities/hooks';
import type {
  EffectiveCapability,
  SettingsValues,
} from '@/features/capabilities/types';

interface DraftPolicy {
  enabled: boolean;
  settings: SettingsValues;
}

export default function ChatSettingsPage() {
  const { t } = useI18n();
  const canView = useModuleAccess('capabilities', 'view');
  const canAdmin = useModuleAccess('capabilities', 'admin');
  const chatSettings = useChatSettings();
  const updateChatSettings = useUpdateChatSettings();
  const [retentionDays, setRetentionDays] = useState('90');
  const [retentionError, setRetentionError] = useState<string | null>(null);
  const [retentionInitialised, setRetentionInitialised] = useState(false);
  const catalog = useCapabilitiesCatalog();
  const setPolicy = useSetTenantPolicy();

  const [draft, setDraft] = useState<Record<string, DraftPolicy>>({});
  const [dirtySlugs, setDirtySlugs] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (catalog.data && dirtySlugs.size === 0) {
      const next: Record<string, DraftPolicy> = {};
      for (const entry of catalog.data) {
        next[entry.slug] = {
          enabled: entry.tenant_policy?.enabled ?? entry.default_for_agents,
          settings: entry.tenant_policy?.settings ?? {},
        };
      }
      setDraft(next);
    }
  }, [catalog.data, dirtySlugs.size]);

  useEffect(() => {
    if (!chatSettings.data || retentionInitialised) return;
    setRetentionDays(String(chatSettings.data.retention_days));
    setRetentionInitialised(true);
  }, [chatSettings.data, retentionInitialised]);

  const saveRetention = async () => {
    const value = Number(retentionDays);
    if (!Number.isInteger(value) || value < 0 || value > 3650) {
      setRetentionError(t('admin.settings.chat.retentionInvalid'));
      return;
    }
    try {
      await updateChatSettings.mutateAsync(value);
      setRetentionError(null);
      toast.success(t('admin.settings.chat.retentionSaved'));
    } catch (error) {
      setRetentionError(getErrorMessage(error));
    }
  };

  const markDirty = (slug: string) =>
    setDirtySlugs((previous) => {
      const next = new Set(previous);
      next.add(slug);
      return next;
    });

  const updateDraft = (slug: string, patch: Partial<DraftPolicy>) => {
    setDraft((previous) => ({
      ...previous,
      [slug]: { ...(previous[slug] ?? { enabled: false, settings: {} }), ...patch },
    }));
    markDirty(slug);
  };

  /** Estado efectivo del tenant (draft) para la lista compacta. */
  const effective = useMemo(() => {
    const out: Record<string, EffectiveCapability> = {};
    for (const entry of catalog.data ?? []) {
      const policy = draft[entry.slug];
      out[entry.slug] = {
        enabled: policy?.enabled ?? entry.tenant_policy?.enabled ?? entry.default_for_agents,
        origin: entry.tenant_policy ? 'tenant' : 'platform',
        settings: (policy?.settings ?? entry.tenant_policy?.settings ?? {}) as Record<
          string,
          unknown
        >,
      };
    }
    return out;
  }, [catalog.data, draft]);

  const save = async () => {
    setSaving(true);
    try {
      for (const slug of dirtySlugs) {
        const policy = draft[slug];
        if (!policy) continue;
        await setPolicy.mutateAsync({
          slug,
          input: { enabled: policy.enabled, settings: policy.settings },
        });
      }
      setDirtySlugs(new Set());
      setModalOpen(false);
      toast.success(t('admin.settings.chat.saved') || 'Configuration saved');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (!canView) {
    return (
      <div className="p-6">
        <Card className="p-6 text-sm text-red-600 dark:text-red-400">
          {t('admin.settings.chat.denied')}
        </Card>
      </div>
    );
  }

  if (catalog.isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }

  if (catalog.isError) {
    return (
      <div className="p-6">
        <Card className="p-6 text-sm text-red-600 dark:text-red-400">
          {t('admin.capabilities.loadError') || 'Could not load capability configuration.'}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {t('admin.settings.chat.title') || 'Chat'}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('admin.settings.chat.subtitle') ||
            'Orchestrator functions and tenant default settings.'}
        </p>
      </div>

      <ChatRetentionCard
        retentionDays={retentionDays}
        onRetentionChange={setRetentionDays}
        canAdmin={canAdmin}
        saving={updateChatSettings.isPending}
        error={retentionError}
        isError={chatSettings.isError}
        usage={chatSettings.data?.usage}
        onSave={saveRetention}
      />

      <Card className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              {t('admin.capabilities.title') || 'Capabilities'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('admin.capabilities.manageDescription') ||
                'Activate or deactivate capabilities and adjust their per-level settings.'}
            </p>
          </div>
          <Button onClick={() => setModalOpen(true)} disabled={!canAdmin}>
            {t('admin.capabilities.manage') || 'Manage capabilities'}
          </Button>
        </div>
      </Card>

      <CapabilityCompactList catalog={catalog.data ?? []} effective={effective} />

      <CapabilitiesModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={t('admin.capabilities.title') || 'Capabilities'}
        description={t('admin.capabilities.manageDescription') || ''}
        catalog={catalog.data ?? []}
        effective={effective}
        dirty={dirtySlugs.size > 0}
        renderControl={(entry) => {
          const policy = draft[entry.slug] ?? {
            enabled: entry.default_for_agents,
            settings: {},
          };
          return (
            <Checkbox
              aria-label={capabilityTitle(t, entry)}
              checked={policy.enabled}
              onCheckedChange={(checked) =>
                updateDraft(entry.slug, { enabled: checked === true })
              }
            />
          );
        }}
        renderExtra={(entry) => {
          const hasSchema = !!(
            entry.config_schema?.properties &&
            Object.keys(entry.config_schema.properties).length > 0
          );
          if (!hasSchema) return undefined;
          const policy = draft[entry.slug] ?? {
            enabled: entry.default_for_agents,
            settings: {},
          };
          return (
            <CapabilitySettingsForm
              schema={entry.config_schema}
              values={policy.settings}
              onChange={(values) => updateDraft(entry.slug, { settings: values })}
            />
          );
        }}
        footer={
          <Button onClick={save} disabled={dirtySlugs.size === 0 || saving}>
            {saving && <Spinner className="mr-2 h-4 w-4" />}
            {t('admin.capabilities.save') || 'Save'}
          </Button>
        }
      />
    </div>
  );
}
